import * as React from 'react'

import { Placement, PlacementNS } from '../utils/types'
import { triggerListeners } from '../utils/triggerListeners'
import { useAutofocus } from '../effects/Autofocus'
import { useHotkey } from '../effects/Hotkey'
import { useAnchor, Anchor } from './Anchor'
import { useDelayedCallback } from './DelayedCallback'

import { useTimer } from './Timer'
import { useVisibility } from './Visibility'
import { DEFAULT_RECT } from '../utils/dom'

/* TYPES */

export type OverlayTriggerPlacement = Placement

export namespace OverlayTriggerVariantNS {
  export const click = 'click'
  export const hover = 'hover'
  export const none = 'none'
  export const contextmenu = 'contextmenu'
}

export type OverlayTriggerVariant =
  | typeof OverlayTriggerVariantNS.click
  | typeof OverlayTriggerVariantNS.hover
  | typeof OverlayTriggerVariantNS.none
  | typeof OverlayTriggerVariantNS.contextmenu

export interface OverlayTriggerConfig {
  autoFocus?: boolean
  canFlip?: boolean
  container?: React.SFC<any>
  containerClass?: string
  hideDelay?: number
  showDelay?: number
  hideOnEscape?: boolean
  initialIsVisible?: boolean
  onVisibleChange?: (isVisible: boolean, e: Event) => boolean | undefined
  overlay?: React.ReactElement<{ placement?: OverlayTriggerPlacement }>
  padding?: number
  placement?: OverlayTriggerPlacement
  keepOpenOnOverlayHover?: boolean
  trigger?: OverlayTriggerVariant
}

export interface OverlayTriggerProps extends OverlayTriggerConfig {
  children: React.ReactNode
}

interface RenderProps extends OverlayTriggerConfig {
  children: (props: { overlayTrigger: OverlayTriggerApi }) => {}
}

export interface OverlayTriggerApi {
  overlay: any
  target: Anchor
  portal: React.Ref<HTMLElement>
}

/* HOOK */

export const useOverlayTrigger: (
  config: OverlayTriggerConfig
) => OverlayTriggerApi = ({
  autoFocus = false,
  canFlip = true,
  hideDelay = 0,
  showDelay = 0,
  initialIsVisible = false,
  onVisibleChange = () => true,
  padding = 3,
  placement = PlacementNS.top,
  trigger = OverlayTriggerVariantNS.click,
  keepOpenOnOverlayHover = trigger === OverlayTriggerVariantNS.hover,
}) => {
  const visibility = useVisibility({ initialIsVisible })

  const anchor = useAnchor({
    canFlip,
    padding,
    dependencies: [visibility.isVisible],
    placement,
  })

  const anchorTargetRef = anchor.target.ref

  const anchorOverlayRef = anchor.overlay.ref

  const timer = useTimer({})

  const show = useDelayedCallback({
    after: visibility.show,
    before: (e: Event) => onVisibleChange(true, e),
    delay: showDelay,
    timer,
  })

  const restoreFocus = (event: Event) => {
    const node = event.target as Node
    const target = anchorTargetRef.current
    const overlay = anchorOverlayRef.current

    if (
      event.type === 'keydown' ||
      (target && target.contains(node)) ||
      (overlay && overlay.contains(node))
    ) {
      anchorTargetRef.current && anchorTargetRef.current.focus()
      event.stopPropagation()
    }
  }

  const hide = useDelayedCallback({
    after: visibility.hide,
    before: (e: Event) => {
      const shouldHide = onVisibleChange(false, e)
      shouldHide !== false && restoreFocus(e)
      return shouldHide
    },
    delay: hideDelay,
    timer,
  })

  triggerListeners({
    trigger,
    on: show,
    off: hide,
    scope: anchorTargetRef,
    value: visibility.isVisible,
  })

  keepOpenOnOverlayHover &&
    triggerListeners({
      trigger,
      on: show,
      off: hide,
      scope: anchorOverlayRef,
      value: visibility.isVisible,
    })

  useHotkey({
    key: ['ArrowDown', 'Down'],
    handler: show,
    scope: anchorTargetRef,
  })

  useHotkey({
    key: ['Escape', 'Esc'],
    handler: hide,
    scope: anchorTargetRef,
  })

  useHotkey({
    key: ['Escape', 'Esc'],
    handler: hide,
    scope: anchorOverlayRef,
  })

  useHotkey({
    key: ['Tab'],
    handler: (e: Event) => {
      visibility.hide()
      restoreFocus(e)
      e.preventDefault()
    },
    scope: anchorOverlayRef,
  })

  useAutofocus({ ref: anchorOverlayRef, enabled: autoFocus })

  return {
    portal: anchor.portal,
    overlay: {
      ...visibility,
      ...anchor.overlay,
      hide,
      show,
      position: anchor.position,
    },
    target: anchor.target,
  }
}

/* DECORATOR */

export const withOverlayTrigger = <T extends {}>(
  config: OverlayTriggerConfig
) => (Component: React.ComponentType<T>) => (
  props: T & { overlayTrigger: OverlayTriggerConfig }
) => (
  <Component
    {...props}
    overlayTrigger={useOverlayTrigger({
      ...config,
      ...props.overlayTrigger,
    })}
  />
)

/* CONTEXT */

const noop: (method: string) => Function = method => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in an \`<OverlayTriggerProvider>\``
  )

const context: OverlayTriggerApi = {
  portal: React.createRef<HTMLElement>(),
  overlay: {
    hide: noop('hide'),
    show: noop('show'),
  },
  target: {
    ref: React.createRef(),
    rect: DEFAULT_RECT,
  },
}

export const OverlayTriggerContext = React.createContext(context)

export const OverlayTriggerProvider = ({
  children,
  ...props
}: OverlayTriggerProps) => (
  <OverlayTriggerContext.Provider value={useOverlayTrigger(props)}>
    {children}
  </OverlayTriggerContext.Provider>
)

export const consumeOverlayTrigger = () =>
  React.useContext(OverlayTriggerContext)
